import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { bevestig } from '../../utils/bevestig';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import {
  T, Icon, stitchDialogPaper, stitchGradientBtn, stitchOutlineBtn, stitchSectionLabel,
} from '../ProjectOverview/parts/stitch';

/**
 * De conceptmachine trainen — bewust voor iedereen, niet alleen admin.
 *
 * Zelfde gedachte als de voeding van de Explainer-generator (NOS op 3 e.a.),
 * maar dan levend: het team voegt zelf voorbeelden, scripts en richtlijnen toe
 * die de conceptgenerator als stijlreferentie meekrijgt. Plus één actieve
 * PowerPoint-template waar de gegenereerde presentatie ín gebouwd wordt (het
 * thema/de huisstijl van de template blijft staan).
 */
const KIND_META = {
  richtlijn: { label: 'Richtlijn', bg: '#ede9fe', color: '#6d28d9', uitleg: 'Stijlregel of instructie' },
  voorbeeld: { label: 'Goed voorbeeld', bg: '#dcfce7', color: '#166534', uitleg: 'Concept/inspiratie dat we goed vinden' },
  script: { label: 'Goed script', bg: '#dbeafe', color: '#1d4ed8', uitleg: 'Script dat de toon zet' },
};

function ConceptTrainer({ open, onClose }) {
  const [items, setItems] = useState(null);
  const [template, setTemplate] = useState(null);
  const [busy, setBusy] = useState(false);
  const [vorm, setVorm] = useState({ kind: 'voorbeeld', title: '', content: '', source: '' });
  const fileRef = useRef(null);

  const laad = useCallback(async () => {
    try {
      const [ir, tr] = await Promise.all([
        authFetch(`${AI_ENGINE_URL}/api/v1/concept-training/items`).then((r) => (r.ok ? r.json() : { items: [] })),
        authFetch(`${AI_ENGINE_URL}/api/v1/concept-training/template`).then((r) => (r.ok ? r.json() : { template: null })),
      ]);
      setItems(ir.items || []);
      setTemplate(tr.template || null);
    } catch { setItems([]); }
  }, []);
  useEffect(() => { if (open) laad(); }, [open, laad]);

  const voegToe = async () => {
    if (!vorm.title.trim() || !vorm.content.trim() || busy) return;
    setBusy(true);
    try {
      const r = await authFetch(`${AI_ENGINE_URL}/api/v1/concept-training/items`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(vorm),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Toevoegen mislukt');
      setVorm({ kind: vorm.kind, title: '', content: '', source: '' });
      toast.success('Toegevoegd — de generator gebruikt dit vanaf nu');
      laad();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const verwijder = async (id) => {
    if (!(await bevestig({ titel: 'Trainingsitem verwijderen?', tekst: 'De generator gebruikt dit voorbeeld daarna niet meer. Dit kan niet ongedaan worden gemaakt.', knop: 'Verwijderen', gevaarlijk: true }))) return;
    try {
      const r = await authFetch(`${AI_ENGINE_URL}/api/v1/concept-training/items/${id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
      laad();
    } catch (e) { toast.error(`Verwijderen mislukt: ${e.message}`); }
  };

  const uploadTemplate = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await authFetch(`${AI_ENGINE_URL}/api/v1/concept-training/template`, { method: 'POST', body: fd });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Upload mislukt');
      toast.success('Template actief — nieuwe concepten gebruiken deze opmaak');
      laad();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: stitchDialogPaper }}>
      <DialogTitle sx={{ fontFamily: T.font, fontWeight: 800 }}>
        Conceptmachine trainen
        <Typography variant="caption" sx={{ display: 'block', fontFamily: T.fontBody, color: T.outline, fontWeight: 400 }}>
          Voeg toe wat wij goed vinden — voorbeelden, scripts en richtlijnen sturen elke volgende generatie. Iedereen mag hieraan bijdragen.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {/* PowerPoint-template */}
        <Typography sx={{ ...stitchSectionLabel, mb: 1 }}>PowerPoint-template</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2.5,
          p: 1.5, borderRadius: '0.6rem', bgcolor: T.surfaceLow }}>
          <Icon name="slideshow" size={20} sx={{ color: T.tertiary }} />
          <Box sx={{ flex: 1, minWidth: 200 }}>
            {template ? (
              <>
                <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 700 }}>{template.filename}</Typography>
                <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>
                  geüpload door {template.uploaded_by || 'onbekend'} — concepten worden ín deze opmaak gebouwd
                </Typography>
              </>
            ) : (
              <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>
                Nog geen template — concepten gebruiken de standaard PowerPoint-opmaak. Upload een .pptx
                met jullie huisstijl (masters/layouts); de inhoudslides worden telkens opnieuw gevuld.
              </Typography>
            )}
          </Box>
          <input ref={fileRef} type="file" hidden accept=".pptx"
            onChange={(e) => { uploadTemplate(e.target.files?.[0]); e.target.value = ''; }} />
          <Box component="span" onClick={() => fileRef.current?.click()} sx={{ ...stitchOutlineBtn, fontSize: '0.78rem' }}>
            <Icon name="upload" size={15} /> {template ? 'Vervang template' : 'Upload template'}
          </Box>
        </Box>

        {/* Nieuw trainingsitem */}
        <Typography sx={{ ...stitchSectionLabel, mb: 1 }}>Nieuw trainingsitem</Typography>
        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' }, mb: 1 }}>
          <TextField select size="small" label="Soort" value={vorm.kind}
            onChange={(e) => setVorm((v) => ({ ...v, kind: e.target.value }))}>
            {Object.entries(KIND_META).map(([k, m]) => (
              <MenuItem key={k} value={k}>{m.label} — {m.uitleg}</MenuItem>
            ))}
          </TextField>
          <TextField size="small" label="Titel" placeholder="Bijv. Concept Achmea werving 2026 (won de pitch)"
            value={vorm.title} onChange={(e) => setVorm((v) => ({ ...v, title: e.target.value }))} />
        </Box>
        <TextField fullWidth multiline minRows={4} maxRows={14} size="small"
          label="Inhoud (plak de tekst — het concept, script of de regel zelf)"
          value={vorm.content} onChange={(e) => setVorm((v) => ({ ...v, content: e.target.value }))} sx={{ mb: 1 }} />
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2.5 }}>
          <TextField size="small" label="Bron (optioneel — link of bestandsnaam)" sx={{ flex: 1 }}
            value={vorm.source} onChange={(e) => setVorm((v) => ({ ...v, source: e.target.value }))} />
          <Box component="span" onClick={voegToe}
            sx={{ ...stitchGradientBtn, opacity: (!vorm.title.trim() || !vorm.content.trim() || busy) ? 0.5 : 1 }}>
            {busy ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <Icon name="add" size={16} />}
            Toevoegen
          </Box>
        </Box>

        {/* Bestaande items */}
        <Typography sx={{ ...stitchSectionLabel, mb: 1 }}>
          Wat de machine nu kent {items ? `(${items.length})` : ''}
        </Typography>
        {items === null ? (
          <CircularProgress size={18} />
        ) : items.length === 0 ? (
          <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline }}>
            Nog leeg — het eerste goede voorbeeld maakt meteen verschil.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {items.map((i) => {
              const m = KIND_META[i.kind] || KIND_META.voorbeeld;
              return (
                <Box key={i.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start',
                  p: 1.25, borderRadius: '0.6rem', border: `1px solid ${T.surfaceHigh}` }}>
                  <Chip size="small" label={m.label}
                    sx={{ height: 20, fontSize: 10.5, fontWeight: 800, bgcolor: m.bg, color: m.color, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontFamily: T.fontBody, fontWeight: 700 }}>{i.title}</Typography>
                    <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {i.content}
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outlineVariant, display: 'block' }}>
                      {i.created_by || 'onbekend'}{i.source ? ` · ${i.source}` : ''}
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => verwijder(i.id)} title="Verwijderen">
                    <Icon name="delete_outline" size={17} />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Box component="span" onClick={onClose} sx={stitchGradientBtn}>Klaar</Box>
      </DialogActions>
    </Dialog>
  );
}

export default ConceptTrainer;
